import React, { useEffect, useRef, useState } from "react";
import { isValidSocialUrl } from "../../../../../utils/utils";
import { HubCard, SecondaryBtn, Ring, useEditLayout } from "../editHubPieces";
import { BmSheet, Btn } from "../../../../ForBrands/ui/BrandUI";
import { HubGroup, SectionRow } from "../../../../ForBrands/ui/mobile/MobileUI";
import { SOCIAL_NETWORKS, normalizeUrl, platformOf } from "../editHubModel";
import { getOnboarding, recomputeSelf, waitFor } from "../editHubServices";
import { fmtK } from "../../onboarding/onboardingPieces";
import { socialPending, watchSocialScore } from "../../socialScoreWatch";
import logoIg from "../../../../../assets/brand/onboarding/logo-instagram-22.svg";
import logoTt from "../../../../../assets/brand/onboarding/logo-tiktok-22.svg";
import logoYt from "../../../../../assets/brand/onboarding/logo-youtube-22.svg";
import logoX from "../../../../../assets/brand/onboarding/logo-x-22.svg";
import checkBig from "../../../../../assets/brand/onboarding/check-12x9.svg";
import menuIg from "../../../../../assets/brand/edit-hub/menu-instagram.svg";
import menuTt from "../../../../../assets/brand/edit-hub/menu-tiktok.svg";
import menuYt from "../../../../../assets/brand/edit-hub/menu-youtube.svg";
import menuX from "../../../../../assets/brand/edit-hub/menu-x.svg";
import menuLink from "../../../../../assets/brand/edit-hub/menu-link.svg";

// v2 · 05 · Socials & links: one row per connected platform, "Add a
// platform" opens the menu (05a), a new row appears empty (05b). A saved
// network link turns green once the profile has been read (followers).
// On phones the menu is a bottom sheet (2104:15542) and the row picked from
// it takes focus, ready for the link. 247Sports and On3 are two platforms
// (a player can have both); a saved link sits under the one its host names.
const PLATFORMS = [
  { key: "instagram", label: "Instagram", group: "networks", logo: logoIg, menu: menuIg, placeholder: "instagram.com/username", tile: "bg-[#e1306c]" },
  { key: "tiktok", label: "TikTok", group: "networks", logo: logoTt, menu: menuTt, placeholder: "tiktok.com/@username", tile: "bg-bm-ink" },
  { key: "youtube", label: "YouTube", group: "networks", logo: logoYt, menu: menuYt, placeholder: "youtube.com/@channel", tile: "bg-[#ff0000]" },
  { key: "x", label: "X", group: "networks", logo: logoX, menu: menuX, placeholder: "x.com/username", tile: "bg-bm-ink" },
  { key: "hudl", label: "Hudl", group: "sports", logo: menuLink, menu: menuLink, placeholder: "hudl.com/profile/…" },
  { key: "247sports", label: "247Sports", group: "sports", logo: menuLink, menu: menuLink, placeholder: "247sports.com/player/…" },
  { key: "on3", label: "On3", group: "sports", logo: menuLink, menu: menuLink, placeholder: "on3.com/db/…" },
  { key: "passport", label: "Passport", group: "sports", logo: menuLink, menu: menuLink, placeholder: "the-passport.com/…" },
  { key: "other", label: "Other (Press, Highlights, etc.)", group: "sports", logo: menuLink, menu: menuLink, placeholder: "https://…" },
];
const byKey = Object.fromEntries(PLATFORMS.map((p) => [p.key, p]));
const shown = (url) => (url || "").replace(/^https?:\/\/(www\.)?/i, "");

// `focusKey` (phones): a new number moves focus to the row's field and
// scrolls it to the middle of the screen, clear of the AutosaveBar.
// `looking` is "slow" while a first read runs past the quick lookup.
export const READING_ACCOUNT_TEXT = "Reading your account. This can take a few minutes, and your visibility score updates once it is done.";
const Row = ({ p, url, found, looking, name, initials, onCommit, onClear, focusKey = 0 }) => {
  const [draft, setDraft] = useState(shown(url));
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  useEffect(() => { setDraft(shown(url)); }, [url]);
  useEffect(() => {
    const el = inputRef.current;
    if (!focusKey || !el) return;
    el.focus({ preventScroll: true });
    if (typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "center" });
  }, [focusKey]);
  const commit = () => {
    const text = draft.trim();
    if (!text) { if (url) onClear(); return; }
    const full = normalizeUrl(text);
    if (!isValidSocialUrl(full) || (p.key !== "other" && platformOf(full) !== p.key)) {
      // "an On3", "an Instagram", "an X" (said "ex"); "a 247Sports", "a TikTok".
      setError(p.key === "other" ? "Enter a full link to a profile, press page or highlight reel." : `That does not look like ${/^([aeiou]|x$)/i.test(p.label) ? "an" : "a"} ${p.label} link.`);
      return;
    }
    setError("");
    if (full !== url) onCommit(full);
  };
  const state = error ? "border border-bm-critical" : found ? "border-2 border-bm-positive" : looking ? "border-2 border-bm-ink" : "border border-bm-border focus-within:border-bm-ink focus-within:ring-1 focus-within:ring-inset focus-within:ring-bm-ink";
  return (
    <div className="flex flex-col gap-[10px]">
      <div className="flex h-[43px] items-center gap-2"><img src={p.logo} alt="" className="h-[22px] w-[22px]" /><span className="bm-overline">{p.label}</span></div>
      <div className={`flex h-14 items-center gap-[10px] rounded-[12px] bg-white pl-[18px] pr-4 ${state}`}>
        <input ref={inputRef} className="ob-bare min-w-0 flex-1" value={draft} placeholder={p.placeholder} onChange={(e) => { setDraft(e.target.value); if (error) setError(""); }} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }} aria-label={`${p.label} link`} />
        {found && <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-bm-positive"><img src={checkBig} alt="" className="h-[9px] w-3" /></span>}
        {looking && <span className="h-6 w-6 shrink-0 animate-spin rounded-full border-[3px] border-bm-mutedbg border-t-bm-orange" />}
      </div>
      {error && <p className="bm-body-s text-bm-critical">{error}</p>}
      {looking && !error && <p className="bm-body-s text-bm-muted">{looking === "slow" ? READING_ACCOUNT_TEXT : "Looking up the profile…"}</p>}
      {found && (
        <div className="flex items-center gap-[10px] rounded-[8px] bg-bm-subtle px-[14px] py-3">
          <span className={`flex h-8 w-8 items-center justify-center rounded-full bm-label-xs text-white ${p.tile || "bg-bm-ink"}`}>{initials}</span>
          <div className="flex min-w-0 flex-1 flex-col gap-px"><span className="bm-label-l text-bm-ink">{name}</span><span className="bm-body-s text-bm-muted">{fmtK(found.followers)} followers · public account</span></div>
          <button type="button" onClick={onClear} className="bm-label-xs text-bm-orange hover:underline">Not me</button>
        </div>
      )}
    </div>
  );
};

// The recompute is throttled per athlete and may answer 202 'scheduled' with
// runsInSeconds, or 429 with retryInSeconds when a run is already queued. The
// queued run reads the links saved by then, so the lookup polls that much
// longer (capped).
const MAX_EXTRA_WAIT_S = 120;
export const extraWaitSeconds = (answer) => {
  const s = Number(answer?.runsInSeconds || answer?.retryInSeconds) || 0;
  return Math.min(MAX_EXTRA_WAIT_S, Math.max(0, s));
};

// `editingOther` (a Circle member editing): the links save, but there is no
// follower lookup. It reads and rescores the signed-in account's own
// onboarding state, which is not the athlete's.
export const SocialsSection = ({ a, set, setAndSave, complete, editingOther = false }) => {
  const { phone } = useEditLayout();
  const urls = a.socialMedia || [];
  const [pending, setPending] = useState([]); // platform keys added from the menu with no link yet
  const [menuOpen, setMenuOpen] = useState(false);
  const [focusRow, setFocusRow] = useState(null); // phones: { id, n } of the row picked last
  const [platforms, setPlatforms] = useState({});
  const [looking, setLooking] = useState({});
  const menuRef = useRef(null);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const name = `${a.name || ""} ${a.surname || ""}`.trim();
  const initials = `${(a.name || "")[0] || ""}${(a.surname || "")[0] || ""}`.toUpperCase();

  useEffect(() => {
    if (editingOther) return undefined;
    let alive = true;
    getOnboarding().then((s) => { if (alive) setPlatforms(s.social?.platforms || {}); }).catch(() => {});
    return () => { alive = false; };
  }, [editingOther]);
  useEffect(() => {
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  // Rows: every saved link (by platform) plus the empty rows added from the menu.
  const rows = [];
  PLATFORMS.forEach((p) => {
    if (p.key === "other") return;
    const url = urls.find((u) => platformOf(u) === p.key);
    if (url || pending.includes(p.key)) rows.push({ p, url: url || "", id: p.key });
  });
  urls.filter((u) => platformOf(u) === "other").forEach((u, i) => rows.push({ p: byKey.other, url: u, id: `other-${i}` }));
  pending.filter((k) => k.startsWith("other-")).forEach((k) => rows.push({ p: byKey.other, url: "", id: k }));

  // `saved` settles once the new link is on the server (true) or was refused
  // (false): the scrape reads the stored links, so it only starts after that.
  const lookup = async (key, saved) => {
    if (editingOther || !SOCIAL_NETWORKS.includes(key)) return;
    setLooking((l) => ({ ...l, [key]: true }));
    try {
      if (!(await saved) || !alive.current) return;
      const answer = await recomputeSelf().catch((e) => e?.data || {});
      const extra = extraWaitSeconds(answer);
      const { state: s } = await waitFor((x) => !!x.social?.platforms?.[key], {
        tries: 6 + Math.ceil(extra / 3), delayMs: 3000, alive: () => alive.current,
      });
      if (alive.current && s) setPlatforms(s.social?.platforms || {});
      // A first read takes minutes (socialScoreWatch): keep the row reading
      // in the background until the run is done, then show what it found.
      if (alive.current && s && !s.social?.platforms?.[key] && socialPending(s)) {
        setLooking((l) => ({ ...l, [key]: "slow" }));
        await new Promise((done) => {
          watchSocialScore({
            first: s,
            read: getOnboarding,
            alive: () => alive.current,
            onLanded: (x) => { if (alive.current) setPlatforms(x.social?.platforms || {}); },
            onPending: (on) => { if (!on) done(); },
          });
        });
      }
    } catch {
      // Best effort: the row keeps its link and shows no follower count.
    } finally {
      if (alive.current) setLooking((l) => ({ ...l, [key]: false }));
    }
  };
  const commit = (row, full) => {
    const rest = row.url ? urls.filter((u) => u !== row.url) : urls.filter((u) => row.p.key === "other" || platformOf(u) !== row.p.key);
    const patch = { socialMedia: [...rest, full] };
    const saved = setAndSave ? setAndSave(patch) : (set(patch), true);
    setPending((p) => p.filter((k) => k !== row.id));
    lookup(row.p.key, saved);
  };
  const clear = (row) => {
    set({ socialMedia: urls.filter((u) => u !== row.url) });
    setPending((p) => p.filter((k) => k !== row.id));
    setPlatforms((pl) => { const n = { ...pl }; delete n[row.p.key]; return n; });
  };
  const addFromMenu = (key) => {
    setMenuOpen(false);
    const id = key === "other" ? `other-${Date.now()}` : key;
    if (phone) setFocusRow((f) => ({ id, n: (f?.n || 0) + 1 }));
    if (key === "other") { setPending((p) => [...p, id]); return; }
    if (rows.some((r) => r.id === key)) return;
    setPending((p) => [...p, key]);
  };
  const connected = new Set(rows.filter((r) => r.url).map((r) => r.p.key));
  const linkOf = Object.fromEntries(rows.filter((r) => r.url && r.p.key !== "other").map((r) => [r.p.key, shown(r.url)]));
  const groups = [["networks", "Social networks"], ["sports", "Sports & press links"]];

  return (
    <HubCard title="Connected platforms" desc={complete ? "Only what you connect appears on your profile." : "Add a sports link to finish this section."}>
      {rows.length === 0 && <p className="bm-body-m text-bm-muted">No platforms yet. Add Instagram or TikTok to start your visibility score, and a Hudl or Passport link for your game footage.</p>}
      {rows.map((row) => (
        <Row key={row.id} p={row.p} url={row.url} found={row.url && SOCIAL_NETWORKS.includes(row.p.key) && !looking[row.p.key] ? platforms[row.p.key] : null} looking={looking[row.p.key] || false} name={name} initials={initials} onCommit={(full) => commit(row, full)} onClear={() => clear(row)} focusKey={focusRow?.id === row.id ? focusRow.n : 0} />
      ))}
      {phone ? (
        <>
          <Btn variant="secondary" size="lg" className="w-full" onClick={() => setMenuOpen(true)}>Add a platform</Btn>
          {menuOpen && (
            // 05a: the platforms in two groups of ProfileSectionRow-style rows
            // (logo tile, name, the saved link or the web placeholder, the
            // complete ring once connected, chevron). Picking one closes the
            // sheet and adds its row (05b).
            <BmSheet title="Add a platform" onClose={() => setMenuOpen(false)}>
              <div className="flex flex-col gap-5">
                {groups.map(([g, label]) => {
                  const list = PLATFORMS.filter((p) => p.group === g);
                  return (
                    <HubGroup key={g} label={label}>
                      {list.map((p, i) => {
                        const on = connected.has(p.key) && p.key !== "other";
                        return (
                          <SectionRow
                            key={p.key}
                            icon={<img src={p.menu} alt="" className="h-5 w-5" />}
                            title={p.label}
                            support={linkOf[p.key] || p.placeholder}
                            ring={on ? <Ring size={32} complete /> : null}
                            onClick={() => addFromMenu(p.key)}
                            last={i === list.length - 1}
                          />
                        );
                      })}
                    </HubGroup>
                  );
                })}
              </div>
            </BmSheet>
          )}
        </>
      ) : (
        <div ref={menuRef} className="relative self-start">
          <SecondaryBtn onClick={() => setMenuOpen((o) => !o)}>Add a platform</SecondaryBtn>
          {menuOpen && (
            <div className="absolute left-0 top-full z-30 mt-2 flex w-[340px] max-w-[calc(100vw-64px)] flex-col gap-[2px] rounded-[16px] border border-bm-border bg-white p-1 shadow-[0px_12px_32px_rgba(0,0,0,0.10)]">
              {groups.map(([g, label]) => (
                <React.Fragment key={g}>
                  <div className="px-3 pb-1 pt-2"><span className="bm-overline !text-bm-tertiary">{label}</span></div>
                  {PLATFORMS.filter((p) => p.group === g).map((p) => (
                    <button key={p.key} type="button" onClick={() => addFromMenu(p.key)} className="flex h-[52px] w-full items-center gap-3 rounded-[12px] px-3 py-[10px] text-left hover:bg-bm-subtle">
                      <img src={p.menu} alt="" className="h-5 w-5" />
                      <span className="flex-1 bm-label-l text-bm-ink">{p.label}</span>
                      {connected.has(p.key) && p.key !== "other" && <Ring size={32} complete />}
                    </button>
                  ))}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      )}
    </HubCard>
  );
};
